import {
  ApprovalMode,
  ApprovalStrategy,
} from '@ebalance/proto/bfftypes/v1/approval_flow_type_pb'
import {
  DemandAutoConfirmMethod,
  LinkedPageKind,
  WorkflowSection,
} from '@ebalance/proto/bffuser/v1/workflow_template_model_pb'
import { type TFunction } from 'i18next'
import { z } from 'zod'

import { codePointLength } from '~/libs/codePoint'
import {
  allowedApprovalModes,
  findApproverAcrossStages,
  lacksApprovers,
} from '~/pages/AdminWorkflowTemplates/models/approvalSetting'

// ─── Enum schemas ────────────────────────────────────────────────────────────

export const WorkflowSectionSchema = z.nativeEnum(WorkflowSection)

export const LinkedPageKindSchema = z.nativeEnum(LinkedPageKind)

// ─── Sub-step approval setting schema ────────────────────────────────────────

export const SubStepApprovalStageSchema = z.object({
  strategy: z.nativeEnum(ApprovalStrategy),
  approverMemberIds: z.array(z.string()),
})

export const SubStepApprovalSettingFormSchema = z.object({
  mode: z.nativeEnum(ApprovalMode),
  stages: z.array(SubStepApprovalStageSchema),
})

export type SubStepApprovalSettingFormValues = z.infer<
  typeof SubStepApprovalSettingFormSchema
>

const AutomationContextSchema = z.object({
  linkedPageKind: LinkedPageKindSchema.optional(),
  approvalMode: z.number().int().optional(),
})

// ─── Sub-step schema ─────────────────────────────────────────────────────────

export const SubStepSchema = z.object({
  displayOrder: z.number().int().min(1),
  /**
   * name: 空判定は createWorkflowTemplateFormSchema の superRefine 側で行う
   * (大ステップタイトルを差し込んだメッセージを生成するため、 field-level
   * `.min(1)` は持たない。 残すと Zod 既定文言の issue が同一 path に並存する)。
   */
  name: z.string(),
  description: z.string().optional(),
  /**
   * linked_page_kind: 運用者が選んだリンク先画面の種別。
   * UNSPECIFIED (= 0) は「リンクなし」として扱い、undefined と等価。
   */
  linkedPageKind: LinkedPageKindSchema.optional(),
  /**
   * assigneeMemberIds: サブステップ担当者の eauth member_id 一覧 (0..N,
   * spec AC-WFT-010, issue #4550 multi-select 化)。空配列 = 担当者なし。
   * 「指定なし」 chip は frontend UI のみの選択肢で、選択されると
   * assigneeMemberIds は空配列になる (backend / DB は区別しない)。
   * `.default([])` は使わない (zodResolver の input/output 型が分離し
   * useForm<WorkflowTemplateFormValues> の generic と衝突するため、
   * 呼び出し側 (Edit.tsx / BigStepEditor.tsx) が常に明示的に配列を渡す
   * 契約とし、必須 field のまま保つ)。
   */
  assigneeMemberIds: z.array(z.string()),
  /**
   * 承認フロー設定 (spec AC-WFT-010e / 010f)。 入札のサブステップは持たず (undefined)、
   * それ以外は必ず持つ (superRefine で検証。 値を作る箇所は `models/approvalSetting.ts`)。
   */
  approvalSetting: SubStepApprovalSettingFormSchema.optional(),
  // Full PUT retains loaded automation values even while their inputs are hidden.
  demandAutoConfirmMethod: z.number().int().optional(),
  // null records an explicit clear without restoring the loaded RHF default.
  demandAutoConfirmOtherMethod: z.number().int().nullable().optional(),
  planAutoSubmitOffsetMinutes: z.number().int().optional(),
  // Compare against the loaded context on submit, even after reordering rows.
  originalAutomationContext: AutomationContextSchema.optional(),
  // Newly entered forecast values also need provenance on new or reverted rows.
  forecastMethodInputContext: AutomationContextSchema.optional(),
})

export type SubStepFormValues = z.infer<typeof SubStepSchema>

// ─── Workflow template schema ─────────────────────────────────────────────────

const V = 'page:adminWorkflowTemplates.validation'

/**
 * 雛形編集フォームのスキーマ。 エラー文言は spec AC-WFT-015 の一覧 (英語は同 spec の
 * 付録) で、 表示言語に合わせるため t を受け取って組み立てる。
 */
export function createWorkflowTemplateFormSchema(
  t: TFunction,
): z.ZodType<WorkflowTemplateFormValues, WorkflowTemplateFormValues> {
  const bigStepSchema = z.object({
    displayOrder: z.number().int().min(1),
    title: z.string().min(1, t(`${V}.bigStepTitleRequired`)),
    /**
     * subSteps: 0 件判定は superRefine 側で行う (大ステップタイトルを差し込んだ
     * メッセージを生成するため、 field-level `.min(1)` は持たない)。
     */
    subSteps: z.array(SubStepSchema),
  })

  return z
    .object({
      id: z.string().uuid().optional(),
      name: z.string().min(1, t(`${V}.nameRequired`)),
      description: z.string().optional(),
      /**
       * section は雛形では必須 (#4)。
       * 3値制限 (#5: today/tomorrow/dayaftertomorrow) は UI (TEMPLATE_SECTION_ITEMS)
       * と backend usecase (IsWorkflowCaseCreationScope) の多層で担保する。schema は
       * 「必須」のみを強制し、enum 自体は workflow_case と共用するため値域を
       * 変えない (週間/月間/年間 を schema では弾かない)。
       * 必須バリデーションは superRefine で行う (= `.refine()` は TypeScript の
       * infer 型を narrowing してしまい useForm の Resolver 型と不一致になるため)。
       */
      section: WorkflowSectionSchema.optional(),
      bigSteps: z.array(bigStepSchema).min(1, t(`${V}.bigStepsRequired`)),
    })
    .superRefine((data, ctx) => {
      const demandForecastPaths: Array<Array<string | number>> = []
      // #4/#5: section は必須 (UNSPECIFIED / undefined を弾く)
      if (
        data.section === undefined ||
        data.section === WorkflowSection.UNSPECIFIED
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t(`${V}.sectionRequired`),
          path: ['section'],
        })
      }

      if (codePointLength(data.name) > 30) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t(`${V}.nameTooLong`),
          path: ['name'],
        })
      }

      if (data.bigSteps.length > 20) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t(`${V}.bigStepsTooMany`),
          path: ['bigSteps'],
        })
      }

      for (const [bigIdx, bigStep] of data.bigSteps.entries()) {
        // 入力欄の maxLength を経ない値 (保存済みの雛形・貼り付け等) の防御。
        if (codePointLength(bigStep.title) > 20) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t(`${V}.bigStepTitleTooLong`),
            path: ['bigSteps', bigIdx, 'title'],
          })
        }

        // サブステップ系エラーへの大ステップタイトル差し込み (grill Q3)。
        // タイトルが空のときは番号表記 (n 番目の大ステップ) にフォールバックする。
        const bigStepLabel =
          bigStep.title !== ''
            ? t(`${V}.bigStepLabelWithTitle`, { title: bigStep.title })
            : t(`${V}.bigStepLabelWithIndex`, { n: bigIdx + 1 })

        // サブステップ 0 件
        if (bigStep.subSteps.length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t(`${V}.subStepsRequired`, { bigStep: bigStepLabel }),
            path: ['bigSteps', bigIdx, 'subSteps'],
          })
        }

        if (bigStep.subSteps.length > 30) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t(`${V}.subStepsTooMany`),
            path: ['bigSteps', bigIdx, 'subSteps'],
          })
        }

        for (const [subIdx, subStep] of bigStep.subSteps.entries()) {
          if (subStep.linkedPageKind === LinkedPageKind.DEMAND_FORECAST_DAILY) {
            demandForecastPaths.push([
              'bigSteps',
              bigIdx,
              'subSteps',
              subIdx,
              'linkedPageKind',
            ])
          }
          // サブステップ名が空: per-field path (['bigSteps', bigIdx, 'subSteps', subIdx, 'name'])
          // で issue を置く。SubStepEditor.tsx が
          // `errors.bigSteps[bigStepIndex].subSteps[subIndex].name` を読んで
          // 該当 input の invalid 表示 (赤枠 / inline エラー) を出すため、配列
          // レベル (['bigSteps', bigIdx, 'subSteps']) に集約すると inline 表示が
          // 効かなくなる。message は大ステップタイトル差し込み済みの同一文言を維持する
          // (AC-04 / TC-V-06.1)。同一大ステップ内に空 name が複数あれば同一
          // message の issue が複数件出るが、top-level alert 側は
          // `collectFormErrorMessages` が dedupe 済み (`~/libs/formErrors`)。
          if (subStep.name === '') {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t(`${V}.subStepNameRequired`, { bigStep: bigStepLabel }),
              path: ['bigSteps', bigIdx, 'subSteps', subIdx, 'name'],
            })
          }

          if (
            subStep.description !== undefined &&
            codePointLength(subStep.description) > 100
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t(`${V}.subStepDescriptionTooLong`, {
                name: subStep.name,
              }),
              path: ['bigSteps', bigIdx, 'subSteps', subIdx, 'description'],
            })
          }

          if (codePointLength(subStep.name) > 30) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t(`${V}.subStepNameTooLong`),
              path: ['bigSteps', bigIdx, 'subSteps', subIdx, 'name'],
            })
          }

          const approvalPath = [
            'bigSteps',
            bigIdx,
            'subSteps',
            subIdx,
            'approvalSetting',
          ]
          const holdsApprovalSetting =
            allowedApprovalModes(subStep.linkedPageKind).length > 0
          if (holdsApprovalSetting && subStep.approvalSetting === undefined) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: '承認フロー設定を選択してください。',
              path: approvalPath,
            })
          }
          if (
            subStep.linkedPageKind === LinkedPageKind.DEMAND_FORECAST_DAILY &&
            subStep.approvalSetting?.mode === ApprovalMode.AUTO
          ) {
            if (subStep.demandAutoConfirmMethod === undefined) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: t(`${V}.forecastMethodRequired`),
                path: [...approvalPath.slice(0, -1), 'demandAutoConfirmMethod'],
              })
            } else if (
              subStep.demandAutoConfirmMethod ===
                Number(DemandAutoConfirmMethod.CSV_REGISTERED_VALUES) &&
              (subStep.demandAutoConfirmOtherMethod === undefined ||
                subStep.demandAutoConfirmOtherMethod === null)
            ) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: t(`${V}.otherForecastMethodRequired`),
                path: [
                  ...approvalPath.slice(0, -1),
                  'demandAutoConfirmOtherMethod',
                ],
              })
            }
          }
          // 要承認で承認者が未設定 (文言は approval-flow AC-APF-021 と同じ)
          if (
            holdsApprovalSetting &&
            subStep.approvalSetting !== undefined &&
            lacksApprovers(subStep.approvalSetting)
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: '承認者を選択してください',
              path: approvalPath,
            })
          }
          const sharedApprover =
            holdsApprovalSetting && subStep.approvalSetting !== undefined
              ? findApproverAcrossStages(subStep.approvalSetting)
              : undefined
          if (sharedApprover !== undefined) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `同じ承認者が ${sharedApprover.firstStage} 段目と ${sharedApprover.secondStage} 段目の両方に登録されています。同じ人が複数の段を承認できると二重チェックになりません。どちらか一方から外してください。`,
              path: approvalPath,
            })
          }
        }
      }
      if (demandForecastPaths.length > 1) {
        for (const path of demandForecastPaths) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t(`${V}.demandForecastAtMostOne`),
            path,
          })
        }
      }
    })
}

export type BigStepFormValues = {
  displayOrder: number
  title: string
  subSteps: SubStepFormValues[]
}

export type WorkflowTemplateFormValues = {
  id?: string | undefined
  name: string
  description?: string | undefined
  section?: WorkflowSection | undefined
  bigSteps: BigStepFormValues[]
}
